import { ArrowLeft, ArrowRight, Check, House, Monitor, QrCode, ScanLine, Smartphone, Star } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../assets/scss/pages/SeedingDownload.module.scss';
import logo from '../../assets/img/logo.png';
import homeapp from '../../assets/img/homeapp.png';
import orderapp from '../../assets/img/orderapp.png';
import billapp from '../../assets/img/billapp.png';
import { version } from '../../config/version-code';

const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.nhatzonz.navosotiemapp&hl=vi&pli=1';
const IOS_URL = 'https://apps.apple.com/us/app/navo-s%E1%BB%95-ti%E1%BB%87m/id6754331504';

const PLATFORMS = [
    { key: 'android', Icon: Monitor, label: 'Android', store: 'Google Play', url: ANDROID_URL, qrAlt: 'QR tải app cho Android' },
    { key: 'ios', Icon: Smartphone, label: 'iPhone / iPad', store: 'App Store', url: IOS_URL, qrAlt: 'QR tải app cho iOS' },
];

const FEATURES = [
    'Bán hàng, tạo hoá đơn & quản lý đơn ngay trên điện thoại',
    'Quản lý khách hàng, tích điểm và chăm sóc tự động',
    'Báo cáo doanh thu theo ngày, tuần, tháng mọi lúc mọi nơi',
    'Quản lý nhân viên, ca làm, hoa hồng & lương rõ ràng',
];

function qrSrc(url) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=0&data=${encodeURIComponent(url)}`;
}

export default function SeedingDownload() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const os = searchParams.get('os');
    const platforms = os === 'android' || os === 'ios' ? PLATFORMS.filter((p) => p.key === os) : PLATFORMS;
    const single = platforms.length === 1;

    const renderQr = (p, large) => (
        <div key={p.key} className={`${styles.qrCard}${large ? ` ${styles.qrCardLarge}` : ''}`}>
            <div className={styles.qrCardHeader}>
                <p.Icon size={16} /> {p.label}
            </div>
            <a href={p.url} target="_blank" rel="noreferrer" className={styles.qrImageWrap}>
                <img src={qrSrc(p.url)} alt={p.qrAlt} className={styles.qrImage} />
            </a>
            <span className={styles.qrCardHint}>{p.store}</span>
        </div>
    );

    const renderBtn = (p) => (
        <a key={p.key} href={p.url} target="_blank" rel="noreferrer" className={styles.storeBtn}>
            <p.Icon className={styles.storeBtnIcon} />
            <span className={styles.storeBtnText}>
                <span className={styles.storeBtnSmall}>Tải về trên</span>
                <span className={styles.storeBtnBig}>{p.store}</span>
            </span>
            <ArrowRight size={18} className={styles.storeBtnArrow} />
        </a>
    );

    const scanHint = (
        <div className={styles.scanHint}>
            <ScanLine size={16} /> Mở camera và hướng vào mã QR để tải
        </div>
    );

    return (
        <div className={styles.page}>
            {/* ===== LEFT PANEL: HERO VISUAL ===== */}
            <div className={styles.left}>
                <div className={styles.leftOverlay} />
                <div className={styles.leftContent}>
                    <div className={styles.brand}>
                        <img src={logo} alt="Navo Sổ Tiệm" className={styles.brandLogo} />
                        <span className={styles.brandName}>Navo Sổ Tiệm</span>
                    </div>

                    <div className={styles.heroText}>
                        <h1 className={styles.heroTitle}>
                            Quản lý tiệm thông minh
                            <span className={styles.heroTitleGradient}> ngay trên điện thoại</span>
                        </h1>
                        <p className={styles.heroSubtitle}>
                            Tải ứng dụng Navo Sổ Tiệm để bán hàng, quản lý khách hàng và xem báo cáo mọi lúc, mọi nơi.
                        </p>
                        <div className={styles.rating}>
                            <span className={styles.stars}>
                                <Star fill="currentColor" />
                                <Star fill="currentColor" />
                                <Star fill="currentColor" />
                                <Star fill="currentColor" />
                                <Star fill="currentColor" />
                            </span>
                            <span className={styles.ratingText}>4.9/5 · Hơn 5.000 tiệm tin dùng</span>
                        </div>
                    </div>

                    <div className={styles.phones}>
                        <div className={`${styles.phone} ${styles.phoneLeft}`}>
                            <img src={orderapp} alt="Giao diện đặt món" />
                        </div>
                        <div className={`${styles.phone} ${styles.phoneCenter}`}>
                            <img src={homeapp} alt="Trang chủ ứng dụng" />
                        </div>
                        <div className={`${styles.phone} ${styles.phoneRight}`}>
                            <img src={billapp} alt="Hoá đơn" />
                        </div>
                    </div>

                    <div className={styles.leftFooter}>&copy; 2025 Navo Sổ Tiệm. Phiên bản: {version}</div>
                </div>
            </div>

            {/* ===== RIGHT PANEL: DOWNLOAD ===== */}
            <div className={styles.right}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} /> Quay lại
                </button>
                <button className={styles.homeBtn} onClick={() => navigate('/')}>
                    <House size={16} /> Trang chủ
                </button>

                <div className={styles.rightInner}>
                    <div className={styles.downloadBadge}>
                        <Smartphone size={15} /> Tải ứng dụng miễn phí
                    </div>
                    <h2 className={styles.title}>Tải Navo Sổ Tiệm về máy</h2>
                    <p className={styles.subtitle}>
                        Dùng camera điện thoại quét mã QR bên dưới, hoặc bấm nút tải tương ứng với thiết bị của bạn.
                    </p>

                    {single ? (
                        /* Single platform: QR + download button on the same row */
                        <div className={styles.singleWrap}>
                            <div className={styles.singleQrCol}>
                                {renderQr(platforms[0], true)}
                                {scanHint}
                            </div>
                            <div className={styles.singleSide}>
                                {renderBtn(platforms[0])}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* QR codes */}
                            <div className={styles.qrRow}>{platforms.map((p) => renderQr(p, false))}</div>
                            {scanHint}
                            {/* Store buttons */}
                            <div className={styles.storeButtons}>{platforms.map((p) => renderBtn(p))}</div>
                        </>
                    )}

                    {/* Feature checklist */}
                    <ul className={styles.features}>
                        {FEATURES.map((f) => (
                            <li key={f} className={styles.featureItem}>
                                <span className={styles.featureIcon}>
                                    <Check size={13} strokeWidth={3} />
                                </span>
                                {f}
                            </li>
                        ))}
                    </ul>

                    <div className={styles.qrFallbackNote}>
                        <QrCode size={14} /> Cần hỗ trợ? Gọi Hotline 0886.299.670
                    </div>
                </div>
            </div>
        </div>
    );
}
